import { type ReactNode, useEffect, useState } from 'react';
import { useStore } from '../lib/store.ts';
import { refreshPlan } from '../hooks/usePlan.ts';
import { EMAIL_UNVERIFIED_EVENT, needsEmailVerification } from '../lib/verification.ts';
import { VerifyEmail } from './VerifyEmail.tsx';

interface VerificationGateProps {
  /** The rider chose "Use a different email" (already signed out). */
  onSignOut: () => void;
  /** The email was just verified (refetch what the gate refused). */
  onVerified?: () => void;
  children: ReactNode;
}

/**
 * Shows "Check your email" instead of the app for an unverified email-only
 * account (``/auth/me``), or after any API call answered 403
 * ``email_unverified`` (docs/EMAIL_VERIFICATION_PLAN.md §3).
 */
export function VerificationGate({ onSignOut, onVerified, children }: Readonly<VerificationGateProps>) {
  const user = useStore((s) => s.user);
  const [forced, setForced] = useState(false);

  // A user saved by an older build (or just registered) may be stale: ask
  // the server. Verified users stay verified, so they skip the call.
  const knownVerified = user?.email_verified === true;
  useEffect(() => {
    if (!knownVerified) void refreshPlan();
  }, [knownVerified]);

  useEffect(() => {
    const show = () => setForced(true);
    globalThis.addEventListener(EMAIL_UNVERIFIED_EVENT, show);
    return () => globalThis.removeEventListener(EMAIL_UNVERIFIED_EVENT, show);
  }, []);

  if (forced || needsEmailVerification(user)) {
    return (
      <VerifyEmail
        email={user?.email ?? ''}
        onVerified={() => {
          setForced(false);
          onVerified?.();
        }}
        onSignOut={onSignOut}
      />
    );
  }
  return <>{children}</>;
}
